import {
  ApplicantArticle,
  ApplicantAward,
  ApplicantCommunity,
  ApplicantCompany,
  ApplicantContest,
  ApplicantEntrepreneurship,
  ApplicantMentionInMedia,
  ApplicantMentorship,
  ApplicantOpenSource,
  ApplicantPatent,
  ApplicantPublicTalk,
  ApplicantScientificWork,
} from "@prisma/client";
import Blob from "cross-blob";
import {
  Document,
  HeadingLevel,
  ISectionOptions,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import flattenDeep from "lodash/flattenDeep";

import {
  ExperienceType,
  getExperienceSectionTitle,
} from "@/lib/experienceType";
import { ApplicationFull } from "@/lib/prisma";

import { docStyles, renderDateRange, sortItemsByDate } from "./common";
import { numberingStyle } from "./common/numbering";
import { DocGenerator } from "../";
import { renderProseMirrorSchemaToGoogleDoc } from "../proseMirror";
import { parseProseMirrorSchema } from "../../proseMirror";

interface SelfServiceCVDocArgs {
  application: ApplicationFull;
  applicantCompanyList: ApplicantCompany[];
  applicantEntrepreneurshipList: ApplicantEntrepreneurship[];
  applicantArticleList: ApplicantArticle[];
  applicantPublicTalkList: ApplicantPublicTalk[];
  applicantContestList: ApplicantContest[];
  applicantAwardList: ApplicantAward[];
  applicantPatentList: ApplicantPatent[];
  applicantOpenSourceList: ApplicantOpenSource[];
  applicantMentorshipList: ApplicantMentorship[];
  applicantCommunityList: ApplicantCommunity[];
  applicantScientificWorkList: ApplicantScientificWork[];
  applicantMentionInMediaList: ApplicantMentionInMedia[];
}

export const createSelfServiceCVDoc: DocGenerator<
  SelfServiceCVDocArgs
> = async (
  t,
  {
    application,
    applicantCompanyList,
    applicantEntrepreneurshipList,
    applicantArticleList,
    applicantPublicTalkList,
    applicantContestList,
    applicantAwardList,
    applicantPatentList,
    applicantOpenSourceList,
    applicantMentorshipList,
    applicantCommunityList,
    applicantScientificWorkList,
    applicantMentionInMediaList,
  }
) => {
  const document = new Document({
    styles: docStyles,
    numbering: numberingStyle,
    sections: [
      {
        children: flattenDeep<ISectionOptions["children"][0] | null>([
          new Paragraph({
            children: [
              new TextRun({
                text: "CV - ",
              }),
              new TextRun({
                text: application.applicant.fullName
                  ? application.applicant.fullName
                  : `${application.applicant.firstName} ${application.applicant.lastName}`,
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),
          [
            {
              name: "Work Experience",
              items: applicantCompanyList,
            },
            {
              name: "Entrepreneurship",
              items: applicantEntrepreneurshipList,
            },
          ].map(({ name, items }) =>
            items.length
              ? [
                  new Paragraph({
                    style: "wellSpaced",
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: name,
                      }),
                    ],
                    heading: HeadingLevel.HEADING_1,
                  }),
                  ...sortItemsByDate<typeof items[0]>(items).map((item) => [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: parseProseMirrorSchema(item.name),
                        }),
                      ],
                      heading: HeadingLevel.HEADING_2,
                    }),

                    renderProseMirrorSchemaToGoogleDoc(
                      item.briefDescription,
                      "-"
                    ),

                    new Paragraph({
                      children: [
                        new TextRun({
                          text: parseProseMirrorSchema(item.role),
                        }),
                      ],
                      heading: HeadingLevel.HEADING_3,
                    }),
                    new Paragraph({
                      children: renderDateRange(
                        item.dateFrom,
                        item.dateTo,
                        item.workingNow
                      ),
                    }),

                    renderProseMirrorSchemaToGoogleDoc(
                      item.responsibility,
                      "-"
                    ),

                    new Paragraph({
                      style: "wellSpaced",
                    }),
                  ]),
                ]
              : null
          ),

          new Paragraph({
            children: [
              new TextRun({
                text: "Media recognition",
              }),
            ],
            heading: HeadingLevel.HEADING_1,
          }),

          [
            {
              name: t(getExperienceSectionTitle(ExperienceType.PublicTalks)),
              items: applicantPublicTalkList,
              getRows: (x: ApplicantPublicTalk) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Year",
                  value: String(x.year),
                },
                {
                  name: "Number of participants",
                  value: x.numbersOfAttendee,
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(getExperienceSectionTitle(ExperienceType.Articles)),
              items: applicantArticleList,
              getRows: (x: ApplicantArticle) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Year",
                  value: String(x.year),
                },
                {
                  name: "Number of views",
                  value: x.numbersOfAttendee,
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(getExperienceSectionTitle(ExperienceType.Contests)),
              items: applicantContestList,
              getRows: (x: ApplicantContest) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Year",
                  value: String(x.year),
                },
                {
                  name: "Your role",
                  value: x.role,
                },
                {
                  name: "Place (if applicable)",
                  value: x.place,
                },
                {
                  name: "Number of participants",
                  value: x.numbersOfAttendee,
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(getExperienceSectionTitle(ExperienceType.Award)),
              items: applicantAwardList,
              getRows: (x: ApplicantAward) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Year",
                  value: String(x.year),
                },
                {
                  name: "Your role",
                  value: x.role,
                },
                {
                  name: "Place (if applicable)",
                  value: x.place,
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(
                getExperienceSectionTitle(ExperienceType.TeachingAndMentorship)
              ),
              items: applicantMentorshipList,
              getRows: (x: ApplicantMentorship) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Period",
                  value:
                    x.fromYear && x.toYear
                      ? `${String(x.fromYear)} - ${
                          Number(x.toYear) === 2099 ? "now" : String(x.toYear)
                        }`
                      : "-",
                },
                {
                  name: "Number of students",
                  value: x.studentsNumbers,
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(
                getExperienceSectionTitle(ExperienceType.ScientificWorkOrPhD)
              ),
              items: applicantScientificWorkList,
              getRows: (x: ApplicantScientificWork) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Period",
                  value:
                    x.fromYear && x.toYear
                      ? `${String(x.fromYear)} - ${
                          Number(x.toYear) === 2099 ? "now" : String(x.toYear)
                        }`
                      : "-",
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(
                getExperienceSectionTitle(ExperienceType.CommunityDevelopment)
              ),
              items: applicantCommunityList,
              getRows: (x: ApplicantCommunity) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Period",
                  value:
                    x.fromYear && x.toYear
                      ? `${String(x.fromYear)} - ${
                          Number(x.toYear) === 2099 ? "now" : String(x.toYear)
                        }`
                      : "-",
                },
                {
                  name: "Number of members",
                  value: x.numbersOfAttendee,
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(
                getExperienceSectionTitle(ExperienceType.OpenSourceContribution)
              ),
              items: applicantOpenSourceList,
              getRows: (x: ApplicantOpenSource) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Period",
                  value:
                    x.fromYear && x.toYear
                      ? `${String(x.fromYear)} - ${
                          Number(x.toYear) === 2099 ? "now" : String(x.toYear)
                        }`
                      : "-",
                },
                {
                  name: "Number of stars, likes, forks or downloads",
                  value: x.numbersOfAttendee,
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(getExperienceSectionTitle(ExperienceType.Patent)),
              items: applicantPatentList,
              getRows: (x: ApplicantPatent) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Year",
                  value: String(x.year),
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
            {
              name: t(getExperienceSectionTitle(ExperienceType.MentionInMedia)),
              items: applicantMentionInMediaList,
              getRows: (x: ApplicantMentionInMedia) => [
                {
                  name: "Title",
                  value: parseProseMirrorSchema(x.name),
                },
                {
                  name: "Year",
                  value: String(x.year),
                },
                {
                  name: "Number of views",
                  value: x.numbersOfAttendee,
                },
                {
                  name: "Link",
                  value: parseProseMirrorSchema(x.url),
                },
              ],
            },
          ].map(({ name, items, getRows }) =>
            items.length
              ? [
                  new Paragraph({
                    style: "wellSpaced",
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `${name}: `,
                      }),
                    ],
                    heading: HeadingLevel.HEADING_2,
                  }),
                  items.map((item, itemIndex) => [
                    new Paragraph({
                      style: "wellSpaced",
                    }),
                    items.length > 1
                      ? [
                          new Paragraph({
                            text: `${itemIndex + 1}.`,
                          }),
                        ]
                      : null,
                    getRows(item as any).map(({ name, value }) => [
                      new Paragraph({
                        style: "leftIndent1",
                        children: [
                          new TextRun({
                            text: `${name}: `,
                            bold: true,
                          }),
                          new TextRun({
                            text: value || "-",
                          }),
                        ],
                      }),
                    ]),
                  ]),
                ]
              : null
          ),
        ]).filter((x): x is ISectionOptions["children"][0] => x !== null),
      },
    ],
  });

  return new Blob([await Packer.toBuffer(document)]);
};
